import sokPreprint from '../Downloads/paper/sok_preprint.pdf';

/**
 * @typedef {Object} NewsLink
 * @property {string} label
 * @property {string} url
 */

/**
 * @typedef {Object} NewsItem
 * @property {string} id - Stable identifier beginning with YYYY-MM
 * @property {string} date - YYYY-MM-DD
 * @property {string} dateLabel - Original human-readable date label
 * @property {'paper'|'award'|'people'|'grant'|'release'|'talk'|'service'|'event'|'other'} type
 * @property {string} title
 * @property {string[]} [emphasisLabels] - Exact title substrings rendered in italics
 * @property {string} [summary]
 * @property {string[]} peopleIds - IDs from people.js
 * @property {string[]} publicationIds - IDs from publications.js
 * @property {string[]} subdirectionIds - IDs from researchDirections.js
 * @property {NewsLink[]} links
 * @property {boolean} [featured]
 * @property {string} [expiresOn] - YYYY-MM-DD; removes time-sensitive items from the homepage after this date
 */

/** @type {NewsItem[]} */
export const news = [
  {
    id: '2026-09-pro-neurips-2026-accepted',
    date: '2026-09-24',
    dateLabel: 'Sept. 2026',
    type: 'paper',
    title: 'Our paper "PRO: Enabling Precise and Robust Text Watermark for Open-Source LLMs" has been accepted by NeurIPS 2026 Main Track as poster.',
    emphasisLabels: ['NeurIPS 2026'],
    peopleIds: ['ruimin-sun'],
    publicationIds: ['pro-open-source-llm-watermark-2025'],
    subdirectionIds: ['privacy-preserving-ai'],
    links: [
      {
        label: 'PRO: Enabling Precise and Robust Text Watermark for Open-Source LLMs',
        url: 'https://openreview.net/forum?id=SP0ojrw7bn#discussion'
      }
    ],
  },
  {
    id: '2026-08-samin-vehiclesec-travel-grant',
    date: '2026-08-01',
    dateLabel: 'Aug. 2026',
    type: 'award',
    title: 'Samin Y Chowdhury won the Travel Grant to attend USENIX Symposium on Vehicle Security and Privacy (VehicleSec 26).',
    emphasisLabels: ['USENIX Symposium on Vehicle Security and Privacy (VehicleSec 26)'],
    peopleIds: ['samin-yasar-chowdhury'],
    publicationIds: ['snfuzz-mqtt-sn-2026'],
    subdirectionIds: ['embedded-software-testing', 'automotive-security'],
    links: [],
  },
  {
    id: '2026-08-black-box-medical-llms-pst-presentation',
    date: '2026-08-01',
    dateLabel: 'Aug. 2026',
    type: 'paper',
    title: 'Mohammed presented our paper “Black-Box Behavioral Distillation Breaks Safety Alignment in Medical LLMs” at the 23rd Annual International Conference on Privacy, Security & Trust.',
    peopleIds: ['mohammed-al-duniawi', 'ruimin-sun'],
    publicationIds: ['black-box-behavioral-distillation-medical-llms-2025'],
    subdirectionIds: ['privacy-preserving-ai', 'medical-system-security'],
    links: [
      {
        label: 'Black-Box Behavioral Distillation Breaks Safety Alignment in Medical LLMs',
        url: 'https://arxiv.org/pdf/2512.09403',
      },
    ],
  },
  {
    id: '2026-08-snfuzz-vehiclesec-presentation',
    date: '2026-08-01',
    dateLabel: 'Aug. 2026',
    type: 'paper',
    title: 'Samin presented our paper “WIP: SNFuzz: Gateway-Centric State-Oriented Fuzzing for MQTT-SN.” at the USENIX Symposium on Vehicle Security and Privacy (VehicleSec 26).',
    emphasisLabels: ['USENIX Symposium on Vehicle Security and Privacy (VehicleSec 26)'],
    peopleIds: ['samin-yasar-chowdhury', 'ruimin-sun'],
    publicationIds: ['snfuzz-mqtt-sn-2026'],
    subdirectionIds: ['embedded-software-testing', 'iot-edge-security'],
    links: [
      {
        label: 'WIP: SNFuzz: Gateway-Centric State-Oriented Fuzzing for MQTT-SN.',
        url: 'https://www.usenix.org/system/files/vehiclesec26-de-la-pena.pdf',
      },
    ],
  },
  {
    id: '2026-07-mohammed-provost-completion-fellowship',
    date: '2026-07-01',
    dateLabel: 'July. 2026',
    type: 'award',
    title: 'Mohammed Al Duniawi won the Provost Degree Completion Tuition Fellowship.',
    peopleIds: ['mohammed-al-duniawi'],
    publicationIds: [],
    subdirectionIds: [],
    links: [],
  },
  {
    id: '2026-07-nsf-reu-asset-funded',
    date: '2026-07-01',
    dateLabel: 'July. 2026',
    type: 'grant',
    title: 'Our project “NSF REU SITE: ASSET: Advanced Secured Sensor Enabling Technologies” is funded by NSF CCF (PI: Niki Pissinou, $464,999.00, 10/1/2026-9/30/2029).',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['iot-edge-security'],
    links: [],
  },
  {
    id: '2026-04-ai-security-fundamentals-approved',
    date: '2026-04-01',
    dateLabel: 'April 2026',
    type: 'other',
    title: 'The graduate-level course I proposed “AI Security Fundamentals” has been approved and will be lectured at FIU in Spring’27.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [],
  },
  {
    id: '2025-11-mqtt-fuzzing-survey-ricss-presentation',
    date: '2025-11-01',
    dateLabel: 'Nov. 2025',
    type: 'paper',
    title: 'Samin presented our paper “A Survey for MQTT Fuzzing.” at the Proceedings of the 2025 Workshop on Re-design Industrial Control Systems with Security (RICSS \'25), co-located with CCS’25.',
    emphasisLabels: ["Proceedings of the 2025 Workshop on Re-design Industrial Control Systems with Security (RICSS '25), co-located with CCS’25"],
    peopleIds: ['samin-yasar-chowdhury', 'ruimin-sun'],
    publicationIds: ['mqtt-fuzzing-survey-2025'],
    subdirectionIds: ['industrial-control-security', 'iot-edge-security'],
    links: [
      {
        label: 'A Survey for MQTT Fuzzing.',
        url: 'https://doi.org/10.1145/3733823.3764515',
      },
    ],
  },
  {
    id: '2025-11-ccs-young-scholar-award',
    date: '2025-11-01',
    dateLabel: 'Nov. 2025',
    type: 'award',
    title: 'Ruimin Sun was awarded with CCS Young Scholar by the ACM CCS 2025.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [],
  },
  {
    id: '2025-09-ricss-workshop-ccs',
    date: '2025-09-01',
    dateLabel: 'Sept. 2025',
    type: 'event',
    title: 'RiS3 Lab will host the 3rd RICSS workshop at ACM CCS 2025.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['industrial-control-security'],
    links: [{ label: 'RICSS workshop', url: 'https://ricssworkshop.github.io/' }],
  },
  {
    id: '2025-08-ndss-usenix-security-tpc',
    date: '2025-08-01',
    dateLabel: 'Aug. 2025',
    type: 'service',
    title: 'Ruimin will serve on the program committees for NDSS 2026 and USENIX Security 2026.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [],
  },
  {
    id: '2025-05-acsac-tpc',
    date: '2025-05-01',
    dateLabel: 'May 2025',
    type: 'service',
    title: 'Ruimin will serve on the ACSAC 2025 program committee.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [],
  },
  {
    id: '2025-04-lousindy-dod-csa-fellowship',
    date: '2025-04-01',
    dateLabel: 'April 2025',
    type: 'award',
    title: 'Lousindy Mitton won the DoD CSA Fellowship.',
    peopleIds: ['lousindy-mitton'],
    publicationIds: [],
    subdirectionIds: [],
    links: [],
  },
  {
    id: '2024-12-tushar-best-poster',
    date: '2024-12-01',
    dateLabel: 'Dec. 2024',
    type: 'award',
    title: 'Tushar Nayan won first place and Best Poster Award at the 2024 Warren B. Nelms IoT Conference.',
    peopleIds: ['tushar-nayan'],
    publicationIds: [],
    subdirectionIds: ['on-device-ml-security'],
    links: [
      {
            label: '2024 Warren B. Nelms IoT Conference',
        url: 'https://iot.institute.ufl.edu/iot-conference-24/',
      },
    ],
  },
  {
    id: '2024-12-iot-conference-signature-talk',
    date: '2024-12-01',
    dateLabel: 'Dec. 2024',
    type: 'talk',
    title: 'Ruimin gave a signature talk at the 2024 Warren B. Nelms IoT Conference.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [
      {
        label: '2024 Warren B. Nelms IoT Conference',
        url: 'https://iot.institute.ufl.edu/iot-conference-24/',
      },
    ],
  },
  {
    id: '2024-11-ricss-co-chair',
    date: '2024-11-01',
    dateLabel: 'Nov. 2024',
    type: 'service',
    title: 'Ruimin co-chaired the RICSS workshop at ACM CCS 2024.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['industrial-control-security'],
    links: [{ label: 'RICSS workshop', url: 'https://ricssworkshop.github.io/' }],
  },
  {
    id: '2024-08-tushar-usenix-security-travel-grant',
    date: '2024-08-01',
    dateLabel: 'Aug. 2024',
    type: 'award',
    title: 'Tushar Nayan won the Travel Grant to present his work at USENIX Security.',
    emphasisLabels: ['USENIX Security'],
    peopleIds: ['tushar-nayan'],
    publicationIds: ['on-device-ml-model-extraction-sok-2024'],
    subdirectionIds: ['on-device-ml-security'],
    links: [],
  },
  {
    id: '2024-06-nsf-cici-award',
    date: '2024-06-01',
    dateLabel: 'June. 2024',
    type: 'grant',
    title: 'Our proposal Secure Machine Learning Inference in IoT-driven Analytical Scientific Infrastructure is funded by NSF CICI.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['secure-ai-inference', 'iot-edge-security'],
    links: [
      {
        label: 'Secure Machine Learning Inference in IoT-driven Analytical Scientific Infrastructure',
        url: 'https://www.nsf.gov/awardsearch/showAward?AWD_ID=2419843',
      },
    ],
  },
  {
    id: '2024-06-ricss-workshop-cfp',
    date: '2024-06-01',
    dateLabel: 'June. 2024',
    type: 'event',
    title: "I'm chairing the Re-design Industrial Control Systems with Security (RICSS) Workshop, happening on Oct. 14, 2024, in Salt Lake City, Utah, co-located with ACM CCS 2024. Consider submit your paper CFP.",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['industrial-control-security'],
    links: [
      { label: 'Re-design Industrial Control Systems with Security (RICSS) Workshop', url: 'https://ricssworkshop.github.io/' },
      { label: 'CFP', url: 'https://ricssworkshop.github.io/' },
    ],
    expiresOn: '2024-10-14',
  },
  {
    id: '2024-03-ml-extraction-sok-accepted',
    date: '2024-03-01',
    dateLabel: 'Mar. 2024',
    type: 'paper',
    title: 'Our paper "SoK: All You Need to Know About On-Device ML Model Extraction - The Gap Between Research and Practice" is accepted by Usenix Security 2024. Congratulations Tushar!',
    peopleIds: ['tushar-nayan', 'ruimin-sun'],
    publicationIds: ['on-device-ml-model-extraction-sok-2024'],
    subdirectionIds: ['on-device-ml-security'],
    links: [],
  },
  {
    id: '2024-03-shift-accepted',
    date: '2024-03-01',
    dateLabel: 'Mar. 2024',
    type: 'paper',
    title: 'Our paper "SHiFT: Semi-hosted Fuzz Testing for Embedded Applications" is accepted by Usenix Security 2024. Congratulations Alejandro!',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['shift-2024'],
    subdirectionIds: ['embedded-software-testing'],
    links: [],
  },
  {
    id: '2023-07-admire-center-award',
    date: '2023-07-01',
    dateLabel: 'Jul. 2023',
    type: 'grant',
    title: 'Our ADvanced education and research for Machine learning-driven critical Infrastructure REsilience (ADMIRE) Center (PI: Hadi Amini) is Awarded by DHS. Congratulations to the team!',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['industrial-control-security', 'software-reliability-resilience'],
    links: [],
  },
  {
    id: '2023-07-virtual-plc-best-paper',
    date: '2023-07-01',
    dateLabel: 'Jul. 2023',
    type: 'award',
    title: 'Our paper The Case for Virtual PLC-enabled Honeypot Design has been awarded the Best Paper Award by RICSS @EuroS&P. Congratulations to Samin and Brandon!',
    peopleIds: ['samin-yasar-chowdhury', 'ruimin-sun'],
    publicationIds: ['virtual-plc-honeypot-2023'],
    subdirectionIds: ['industrial-control-security'],
    links: [
      {
        label: 'The Case for Virtual PLC-enabled Honeypot Design',
        url: 'https://www.computer.org/csdl/proceedings-article/eurospw/2023/272000a351/1OFtfz6r864',
      },
    ],
  },
  {
    id: '2023-07-unsafe-behavior-accepted',
    date: '2023-07-01',
    dateLabel: 'Jul. 2023',
    type: 'paper',
    title: 'Our paper Unsafe Behavior Detection with Adaptive Contrastive Learning in Industrial Control Systems has been accepted by RICSS @EuroS&P. Congratulations to the team!',
    peopleIds: ['samin-yasar-chowdhury', 'ruimin-sun'],
    publicationIds: ['unsafe-behavior-detection-ics-2023'],
    subdirectionIds: ['industrial-control-security'],
    links: [
      {
        label: 'Unsafe Behavior Detection with Adaptive Contrastive Learning in Industrial Control Systems',
        url: 'https://www.computer.org/csdl/proceedings-article/eurospw/2023/272000a363/1OFtfAJLVx6',
      },
    ],
  },
  {
    id: '2023-03-pc-service',
    date: '2023-03-01',
    dateLabel: 'Mar. 2023',
    type: 'service',
    title: "I'm serving on the PC of CCS, RAID, SecDev, and ICCCN. Consider submit your paper.",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [],
  },
  {
    id: '2023-01-ricss-workshop-cfp',
    date: '2023-01-01',
    dateLabel: 'Jan. 2023',
    type: 'event',
    title: "I'm chairing the 1st workshop on Re-design Industrial Control Systems with Security (RICSS), happening on July 7, 2023, in Delft, Netherlands, co-located with IEEE EuroS&P 2023. Consider submit your paper CFP.",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['industrial-control-security'],
    links: [
      { label: 'workshop on Re-design Industrial Control Systems with Security (RICSS)', url: 'https://ricssworkshop.github.io/' },
      { label: 'CFP', url: 'https://ricssworkshop.github.io/cfp.html' },
    ],
    expiresOn: '2023-07-07',
  },
  {
    id: '2023-01-nsf-cites-award',
    date: '2023-01-01',
    dateLabel: 'Jan. 2023',
    type: 'grant',
    title: 'Our proposal Trustworthy Lifecycle Modeling of CPS using Formal Methods is funded by NSF CITES.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['industrial-control-security'],
    links: [],
  },
  {
    id: '2022-12-shadownet-forthcoming',
    date: '2022-12-01',
    dateLabel: 'Dec. 2022',
    type: 'paper',
    title: 'Our work ShadowNet: A Secure and Efficient On-device Model Inference System for Convolutional Neural Networks is going to appear in IEEE S&P 2023.',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['shadownet-2023'],
    subdirectionIds: ['on-device-ml-security', 'secure-ai-inference'],
    links: [
      {
        label: 'ShadowNet: A Secure and Efficient On-device Model Inference System for Convolutional Neural Networks',
        url: 'https://www.arxiv.org/abs/2011.05905',
      },
    ],
  },
  {
    id: '2022-06-online-binary-models-accepted',
    date: '2022-06-01',
    dateLabel: 'June 2022',
    type: 'paper',
    title: 'Our work Online Binary Models are Promising for Distinguishing Temporally Consistent Computer Usage Profiles is accepted by IEEE Transactions on Biometrics, Behavior, and Identity Science.',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['online-binary-models-2022'],
    subdirectionIds: [],
    links: [
      {
        label: 'Online Binary Models are Promising for Distinguishing Temporally Consistent Computer Usage Profiles',
        url: 'https://ieeexplore.ieee.org/document/9786768',
      },
    ],
  },
  {
    id: '2022-05-dtrap-guest-editor',
    date: '2022-05-01',
    dateLabel: 'May 2022',
    type: 'service',
    title: "I'm serving as Guest Editor at Special Issue on Memory-based Malware and other Unconventional Threats at ACM Journal Digital Threats: Research and Practice.",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['malware-detection-mitigation'],
    links: [
      { label: 'Digital Threats: Research and Practice', url: 'https://dl.acm.org/journal/dtrap' },
    ],
  },
  {
    id: '2022-04-d-box-presented',
    date: '2022-04-01',
    dateLabel: 'April 2022',
    type: 'paper',
    title: 'Our work D-Box: DMA-enabled compartmentalization for embedded applications is presented at NDSS 2022.',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['d-box-2022'],
    subdirectionIds: ['embedded-isolation'],
    links: [
      {
        label: 'D-Box: DMA-enabled compartmentalization for embedded applications',
        url: 'https://www.ndss-symposium.org/ndss-paper/auto-draft-258/',
      },
    ],
  },
  {
    id: '2022-03-join-fiu',
    date: '2022-03-01',
    dateLabel: 'Mar 2022',
    type: 'people',
    title: "I'm going to join FIU as a tenure-track assistant professor. Send me an email if you are interested in security research.",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [],
  },
  {
    id: '2021-12-d-box-accepted',
    date: '2021-12-01',
    dateLabel: 'Dec 2021',
    type: 'paper',
    title: "Our work D-Box: DMA-enabled compartmentalization for embedded applications is accepted by NDSS'22.",
    peopleIds: ['ruimin-sun'],
    publicationIds: ['d-box-2022'],
    subdirectionIds: ['embedded-isolation'],
    links: [{ label: "NDSS'22", url: 'https://www.ndss-symposium.org/ndss2022/' }],
  },
  {
    id: '2021-03-teach-systems-security',
    date: '2021-03-01',
    dateLabel: 'Mar 2021',
    type: 'other',
    title: "I'm going to teach CS 3740 - Systems Security at Northeastern University.",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [],
  },
  {
    id: '2021-03-ics-sok-forthcoming',
    date: '2021-03-01',
    dateLabel: 'Mar 2021',
    type: 'paper',
    title: "Our Sok study on PLC attacks and formal verifiation-based protection is to appear at EuroS&P'21.",
    peopleIds: ['ruimin-sun'],
    publicationIds: ['ics-attacks-defenses-sok-2021'],
    subdirectionIds: ['industrial-control-security'],
    links: [
      { label: 'Sok study', url: sokPreprint },
      { label: "EuroS&P'21", url: 'http://www.ieee-security.org/TC/EuroSP2021/' },
    ],
  },
  {
    id: '2021-01-on-device-ml-study-forthcoming',
    date: '2021-01-01',
    dateLabel: 'Jan 2021',
    type: 'paper',
    title: "Our large-scale study on the (in)security of on-device machine learning models is to appear at USENIX Security'21..",
    peopleIds: ['ruimin-sun'],
    publicationIds: ['mind-your-weights-2021'],
    subdirectionIds: ['on-device-ml-security'],
    links: [
      { label: 'large-scale study', url: 'https://arxiv.org/pdf/2002.07687.pdf' },
      { label: "USENIX Security'21.", url: 'https://www.usenix.org/conference/usenixsecurity21' },
    ],
  },
  {
    id: '2020-06-mind-your-weights-webinar',
    date: '2020-06-01',
    dateLabel: 'June 2020',
    type: 'talk',
    title: 'Our work on Mind Your Weight(s): A Large-scale Study on Insufficient Machine Learning Model Protection in Mobile Apps is presented at webinar of Future of Privacy Forum (FPF).',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['mind-your-weights-2021'],
    subdirectionIds: ['on-device-ml-security'],
    links: [{ label: '(FPF)', url: 'https://fpf.org/' }],
  },
  {
    id: '2020-05-defensive-programing-accepted',
    date: '2020-05-01',
    dateLabel: 'May 2020',
    type: 'paper',
    title: 'Our work on praise for defensive programing is accepted by TDSC.',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['praise-for-defensive-programming-2020'],
    subdirectionIds: ['malware-detection-mitigation'],
    links: [{ label: 'TDSC', url: 'https://ieeexplore.ieee.org/xpl/RecentIssue.jsp?punumber=8858' }],
  },
  {
    id: '2020-05-mitos-accepted',
    date: '2020-05-01',
    dateLabel: 'May 2020',
    type: 'paper',
    title: 'Our work on MITOS: Optimal Decisioning for the Indirect Flow Propagation Dilemma in Dynamic Information Flow Tracking is accepted by ICDCS.',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['mitos-2020'],
    subdirectionIds: ['information-flow-security'],
    links: [{ label: 'ICDCS', url: 'https://icdcs2020.sg/' }],
  },
  {
    id: '2019-08-join-neu',
    date: '2019-08-01',
    dateLabel: 'Aug 2019',
    type: 'people',
    title: "I'm excited to Join NEU as a Postdoctoral Research Associate.",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [],
  },
  {
    id: '2019-01-sp-student-program-committee',
    date: '2019-01-01',
    dateLabel: 'Jan 2019',
    type: 'service',
    title: "I'm selected as Student Program Committee of S&P 2019.",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [{ label: 'S&P 2019', url: 'https://www.ieee-security.org/TC/SP2019/' }],
  },
  {
    id: '2018-08-issre-program-committee',
    date: '2018-08-01',
    dateLabel: 'Aug 2018',
    type: 'service',
    title: "I'm invited as Program Committee of ISSRE 2018 Fast Abstract Track.",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['software-reliability-resilience'],
    links: [{ label: 'ISSRE 2018', url: 'http://2018.issre.net/' }],
  },
  {
    id: '2018-08-sherlock-talk-vmware',
    date: '2018-08-01',
    dateLabel: 'Aug 2018',
    type: 'talk',
    title: 'I give my talk Sherlock: An AI-based Event-driven System Behavior Diagnosing Approach at VMware',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['software-reliability-resilience'],
    links: [],
  },
  {
    id: '2018-05-vmware-internship',
    date: '2018-05-01',
    dateLabel: 'May 2018',
    type: 'people',
    title: "I'll start my internship researching on malware detection with AI at VMware.",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['malware-detection-mitigation'],
    links: [{ label: 'VMware', url: 'https://www.vmware.com/products/appdefense.html' }],
  },
  {
    id: '2018-02-malware-mitigation-talk',
    date: '2018-02-01',
    dateLabel: 'Feb 2018',
    type: 'talk',
    title: "I'm happy to give my talk - Leveraging Unpredictabilities for Real-time Malware Mitigation at Miami University, Ohio.",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['malware-detection-mitigation'],
    links: [],
  },
  {
    id: '2018-02-tifs-reviewer',
    date: '2018-02-01',
    dateLabel: 'Feb 2018',
    type: 'service',
    title: "I'm invited to be a reviewer of IEEE Transactions on Information Forensics and Security (TIFS).",
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [
      { label: '(TIFS)', url: 'https://ieeexplore.ieee.org/xpl/RecentIssue.jsp?punumber=10206' },
    ],
  },
  {
    id: '2017-10-grace-hopper-scholar',
    date: '2017-10-01',
    dateLabel: 'Oct 2017',
    type: 'award',
    title: 'I attend Grace Hopper Celebration conference as a scholar.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [{ label: 'Grace Hopper Celebration conference', url: 'https://ghc.anitab.org/' }],
  },
  {
    id: '2017-08-dsc-presentation',
    date: '2017-08-01',
    dateLabel: 'Aug 2017',
    type: 'talk',
    title: 'I presented at the DSC conference in Taipei, Taiwan.',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['dose-makes-the-poision-2017'],
    subdirectionIds: ['malware-detection-mitigation'],
    links: [{ label: 'DSC', url: 'https://dsc17.cs.nctu.edu.tw/program' }],
  },
  {
    id: '2017-08-beihang-talk',
    date: '2017-08-01',
    dateLabel: 'Aug 2017',
    type: 'talk',
    title: "I'm giving my talk at Beihang University for my work Understanding Application Sensitivity towards OS (mis)Behavior and How Diverse Environments Improve Software Availability.",
    peopleIds: ['ruimin-sun'],
    publicationIds: ['bear-2016'],
    subdirectionIds: ['software-reliability-resilience'],
    links: [],
  },
  {
    id: '2017-05-grace-hopper-scholarship',
    date: '2017-05-01',
    dateLabel: 'May 2017',
    type: 'award',
    title: 'I received the Scholarship for 2017 Grace Hopper Celebration of Women in Computing.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [],
  },
  {
    id: '2017-05-dose-accepted',
    date: '2017-05-01',
    dateLabel: 'May 2017',
    type: 'paper',
    title: 'Our paper The Dose Makes the Poision - Leveraging Uncertainty for Effective Malware Detection is accepted by DSC 2017.',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['dose-makes-the-poision-2017'],
    subdirectionIds: ['malware-detection-mitigation'],
    links: [],
  },
  {
    id: '2016-08-bear-accepted',
    date: '2016-08-01',
    dateLabel: 'Aug 2016',
    type: 'paper',
    title: 'Our paper Bear: A Framework for Understanding Application Sensitivity to OS (Mis)Behavior is accepted by ISSRE 2016.',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['bear-2016'],
    subdirectionIds: ['software-reliability-resilience'],
    links: [],
  },
  {
    id: '2015-chameleon-story',
    date: '2015-01-01',
    dateLabel: '2015',
    type: 'other',
    title: 'Chameleon story got published on Alligator.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: ['software-reliability-resilience'],
    links: [
      {
        label: 'Alligator',
        url: 'http://www.alligator.org/news/campus/article_b28dc956-c31d-11e5-a1f9-03ae15b38446.html',
      },
    ],
  },
  {
    id: '2015-unpredictability-login',
    date: '2015-01-01',
    dateLabel: '2015',
    type: 'paper',
    title: 'Our paper "The Case for Unpredictability as Deception as OS Features" will appear in the August issue of ;login.',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['unpredictability-as-deception-2015'],
    subdirectionIds: ['software-reliability-resilience'],
    links: [
      { label: ';login', url: 'https://www.usenix.org/blog/login-magazine-usenix-gets-makeover' },
    ],
  },
  {
    id: '2015-grepsec-travel-award',
    date: '2015-01-01',
    dateLabel: '2015',
    type: 'award',
    title: 'I receive the GREPSEC II Travel Award.',
    peopleIds: ['ruimin-sun'],
    publicationIds: [],
    subdirectionIds: [],
    links: [{ label: 'GREPSEC II Travel Award', url: 'http://www.ieee-security.org/grepsec/' }],
  },
  {
    id: '2015-hotos-paper-accepted',
    date: '2015-01-01',
    dateLabel: '2015',
    type: 'paper',
    title: 'Our paper The Case for Less Predictable Operating System Behavior is accepted by HotOS 2015.',
    peopleIds: ['ruimin-sun'],
    publicationIds: ['less-predictable-os-behavior-2015'],
    subdirectionIds: ['software-reliability-resilience'],
    links: [{ label: 'HotOS 2015', url: 'https://www.usenix.org/conference/hotos15' }],
  },
];
